// ============================================================================
// PR 생성 모달 (CHG-046: REQ-V46-010)
// ============================================================================

import { useState } from 'react';
import { X, Loader2, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { useCreateIssuePR } from '../../hooks/useGitHub';
import type { GitHubRepo, GitHubBranchItem } from '../../services/githubService';

interface Props {
  projectId: string;
  issueId: string;
  repos: GitHubRepo[];
  branches: GitHubBranchItem[];
  onClose: () => void;
}

export default function PRCreateModal({ projectId, issueId, repos, branches, onClose }: Props) {
  const [repoId, setRepoId] = useState(repos[0]?.repoId ?? '');
  const [sourceBranch, setSourceBranch] = useState(branches[0]?.name ?? '');
  const [targetBranch, setTargetBranch] = useState('');
  const [title, setTitle] = useState('');
  const [isDraft, setIsDraft] = useState(false);

  const createMutation = useCreateIssuePR(projectId, issueId);
  const selectedRepo = repos.find((r) => r.repoId === repoId);
  const activeBranches = branches.filter((b) => b.status === 'ACTIVE');
  const hasBranches = activeBranches.length > 0;

  const handleCreate = async () => {
    if (!sourceBranch) {
      toast.error('소스 브랜치를 선택하세요.');
      return;
    }
    try {
      await createMutation.mutateAsync({
        repoId,
        sourceBranch,
        targetBranch: targetBranch || undefined,
        title: title || undefined,
        isDraft,
      });
      toast.success('Pull Request가 생성되었습니다.');
      onClose();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'PR 생성에 실패했습니다.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900">Pull Request 생성</h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600"><X className="size-5" /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          {!hasBranches && (
            <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
              <AlertCircle className="size-4 shrink-0" />
              먼저 브랜치를 생성하세요.
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Repository</label>
            <select
              value={repoId}
              onChange={(e) => setRepoId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500"
            >
              {repos.map((r) => (
                <option key={r.repoId} value={r.repoId}>{r.fullName}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">소스 브랜치</label>
            {hasBranches ? (
              <select
                value={sourceBranch}
                onChange={(e) => setSourceBranch(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500 font-mono"
              >
                {activeBranches.map((b) => (
                  <option key={b.branchId} value={b.name}>{b.name}</option>
                ))}
              </select>
            ) : (
              <input
                value={sourceBranch}
                onChange={(e) => setSourceBranch(e.target.value)}
                placeholder="feature/branch-name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500 font-mono"
              />
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">타겟 브랜치</label>
            <input
              value={targetBranch}
              onChange={(e) => setTargetBranch(e.target.value)}
              placeholder={selectedRepo?.defaultBranch ?? 'main'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500"
            />
            <p className="text-sm text-gray-400 mt-1">비워두면 기본 브랜치를 사용합니다.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">제목 (선택)</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="자동 생성: [이슈키] 이슈 제목"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isDraft}
              onChange={(e) => setIsDraft(e.target.checked)}
              className="rounded text-primary-600"
            />
            <span className="text-sm text-gray-700">Draft PR로 생성</span>
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">취소</button>
          <button
            onClick={handleCreate}
            disabled={!repoId || !sourceBranch || createMutation.isPending}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {createMutation.isPending && <Loader2 className="size-4 animate-spin" />}
            PR 생성
          </button>
        </div>
      </div>
    </div>
  );
}
